import { useQueries } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { api, unwrap } from "../api";
import type { HistoryPoint } from "../history/history";
import { mightBeMoving, type MemberRow } from "./liveCircle";
import { useMovingTrailsShown } from "./movingTrails";
import { useTravelTogether } from "./travelTogether";
import { MOVING_TRAIL_WINDOW_MS, movingTrailPoints, trailRange } from "./trail";

/** A new fix refetches a trail once the last attempt is at least this old; newer fixes extend it meanwhile. */
const REFETCH_AFTER_MS = 30_000;

export interface MovingTracks {
  /** User id → the last 15 minutes of their trip under way (`movingTrailPoints`), for each member fetched. */
  tracks: ReadonlyMap<string, HistoryPoint[]>;
  /** "Trails on the map" is on: draw them. */
  shown: boolean;
  /** "Group people travelling together" is on: `travelGroups` may use them. */
  grouping: boolean;
}

/**
 * The last 15 minutes of every member on the move (`movingTrailPoints`), as of
 * `nowMs`, fetched while "Trails on the map" or "Group people travelling
 * together" is on (account page). `excludeId` (the selected member, whose
 * longer trail `useTrail` draws) is left out unless grouping, which needs
 * everyone's track; grouping also fetches every fix (`tolerance_m: 0`), as
 * detection compares fixes in time. Only members who {@link mightBeMoving} are
 * fetched, so a precise location is needed: once one stops, pauses or shares
 * approximately, its history is no longer observed and is dropped
 * (`gcTime: 0`). An open SOS gets its own fetch and a trail from its start
 * only, as in `useTrail`.
 */
export function useMovingTrails(
  circleId: string,
  rows: readonly MemberRow[],
  nowMs: number,
  excludeId: string | null,
): MovingTracks {
  const shown = useMovingTrailsShown();
  const grouping = useTravelTogether();
  const fetched = shown || grouping;
  const moving = useMemo(
    () => (fetched ? rows.filter((row) => (grouping || row.userId !== excludeId) && mightBeMoving(row, nowMs)) : []),
    [fetched, grouping, rows, excludeId, nowMs],
  );
  const results = useQueries({
    queries: moving.map((row) => ({
      queryKey: ["circle", circleId, "moving-trail", row.userId, row.sos?.id ?? null, grouping],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        unwrap(
          await api.GET("/circles/{circleId}/members/{userId}/history", {
            params: {
              path: { circleId, userId: row.userId },
              query: {
                ...trailRange(Date.now(), MOVING_TRAIL_WINDOW_MS),
                ...(grouping ? { tolerance_m: 0 } : {}),
              },
            },
            signal,
          }),
        ),
      staleTime: REFETCH_AFTER_MS,
      gcTime: 0,
      retry: 1,
    })),
  });

  // A fix newer than the one the last fetch began with asks for a refetch, once that
  // attempt (failed or not) is old enough. One that arrives meanwhile stays pending and
  // is reconsidered when the fetch settles or the clock ticks.
  const fetchedFor = useRef(new Map<string, string>());
  const fixKey = moving.map((row) => `${row.userId}@${row.location?.ts}`).join(",");
  const settledKey = results.map((result) => `${result.dataUpdatedAt}/${result.errorUpdatedAt}`).join(",");
  useEffect(() => {
    const next = new Map<string, string>();
    moving.forEach((row, i) => {
      const ts = row.location?.ts;
      const result = results[i];
      if (ts === undefined || !result) return;
      // A member seen for the first time is being fetched with this fix.
      const before = fetchedFor.current.get(row.userId) ?? ts;
      next.set(row.userId, before);
      if (before === ts || result.isFetching) return;
      const attemptedAt = Math.max(result.dataUpdatedAt, result.errorUpdatedAt);
      if (attemptedAt === 0 || Date.now() - attemptedAt < REFETCH_AFTER_MS) return;
      next.set(row.userId, ts);
      void result.refetch();
    });
    fetchedFor.current = next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixKey, settledKey, nowMs]);

  const tracks = useMemo(
    () =>
      new Map(
        moving.flatMap((row, i) => {
          const result = results[i];
          // A failed refetch keeps the old data; no trail is better than a stale one.
          const history = result && result.errorUpdatedAt <= result.dataUpdatedAt ? result.data : undefined;
          const location = row.location;
          if (!history || !location || history.user_id !== row.userId) return [];
          const sosSince = row.sos ? Date.parse(row.sos.since) : undefined;
          return [[row.userId, movingTrailPoints(history, location, nowMs, sosSince)] as const];
        }),
      ),
    // `results` is a new array each render; `settledKey` changes with each settled attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [moving, nowMs, settledKey],
  );
  return useMemo(() => ({ tracks, shown, grouping }), [tracks, shown, grouping]);
}
